import axios from 'axios';

// Create dedicated AI client with longer timeout for AI operations
const aiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8081/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes timeout for AI operations
});

// Request interceptor to add auth token
aiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
aiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 for login/auth related endpoints
    if (error.response?.status === 401 &&
        (error.config?.url?.includes('/auth') ||
         error.config?.url?.includes('/login') ||
         error.config?.url?.includes('/profile'))) {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export interface AiRequest {
  prompt: string;
  genre?: string;
  targetLength?: number;
  improvementType?: string;
  count?: number;
  theme?: string;
  ageRange?: string;
}

export interface AiResponse {
  content: string;
  success: boolean;
  message: string;
  generationTime: number;
}

class AiAPIService {
  async getStatus(): Promise<AiResponse> {
    const response = await aiClient.get('/ai/status');
    return response.data;
  }

  async getModelInfo(): Promise<string> {
    const response = await aiClient.get('/ai/model-info');
    return response.data;
  }

  async generateStory(request: AiRequest): Promise<AiResponse> {
    const response = await aiClient.post('/ai/generate-story', request);
    return response.data;
  }

  async generateStoryIdeas(request: AiRequest): Promise<AiResponse> {
    const response = await aiClient.post('/ai/generate-ideas', request);
    return response.data;
  }

  async summarizeStory(storyContent: string): Promise<AiResponse> {
    const response = await aiClient.post('/ai/summarize', {
      prompt: storyContent
    });
    return response.data;
  }

  async improveStory(storyContent: string, improvementType?: string, ageRange?: string): Promise<AiResponse> {
    const response = await aiClient.post('/ai/improve', {
      prompt: storyContent,
      improvementType: improvementType || 'general',
      ageRange: ageRange || '6-8'
    });
    return response.data;
  }
}

export const aiAPI = new AiAPIService();
export default aiAPI;